import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { Subscription } from 'rxjs';
import { APPOINTMENT_STATUS, PROCEDURE_CONSTANTS } from 'src/app/constants/scheduling.constants';
import { NewHAService } from 'src/app/services/ha-new/new-ha.service';
import { AppointmentsService } from 'src/app/services/scheduling/appointment/appointments.service';
import { SpinnerComponent } from '../../shared/spinner/spinner.component';
import { ProfileService } from '../../../services/core/profile.service';
import { MatStepper } from '@angular/material/stepper';
import { AppUtil } from 'src/app/utils/app.util';
import { Appointment } from 'src/app/beans/dashboard/dashboard-module.bean';
import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { DOCUMENT } from '@angular/common';
import { PageScrollService } from 'ngx-page-scroll-core';

@Component({
	selector: 'app-ha-landing',
	templateUrl: './ha-landing.component.html',
	styleUrls: ['./ha-landing.component.scss'],
})
export class HaLandingComponent implements OnInit {
	/** decides if HA is read only or not */
	readOnly = true;
	/** holds appointment ID */
	apptID: number = 0;
	/** holds Health assessment ID */
	healthAssessmentID: number = 0;
	/** Data carrier subscriptions */
	subscriptions: Subscription[] = [];
	interaction = [true, false, false];
	selectedIndex = 0;
	returningUser = false;
	allowNavigation = true;
	showBanner = false;
	scrollToTopSections = ["general-emergency", "medical-screening", "medical-men-health", "medical-women-health", "lifestyle-work-life-balance"]
	@ViewChild('stepper', { static: true }) stepper: MatStepper;
	@ViewChild('outletGeneral', { static: true }) outletGeneral: RouterOutlet;
	@ViewChild('outletMedical', { static: true }) outletMedical: RouterOutlet;
	@ViewChild('outletLifeStyle', { static: true }) outletLifeStyle: RouterOutlet;

	/**
	 * constructor
	 * @param appointmentService
	 * @param router
	 */
	constructor(
		private appointmentService: AppointmentsService,
		private router: Router,
		private activatedRoute: ActivatedRoute,
		private spinner: SpinnerComponent,
		public haService: NewHAService,
		public profileService: ProfileService,
		private pageScrollService: PageScrollService,
		private route: ActivatedRoute,
		@Inject(DOCUMENT) private document: any
	) {
		this.activatedRoute.queryParams.subscribe(params => {
			if (params && params.haCompleted === 'true') {
				this.completeStepper();
			} else {
				const stepperElement = document.getElementById('mat-stepper');
				if (stepperElement) {
					stepperElement.classList.remove('stepper-done');
				}
			}
		});
	}

	ngOnInit() {
		document.body.classList.add('new-ha');
		this.getUpcomingAppointments();
		if (AppUtil.isIE()) {
			const element = document.getElementById('new-ha');
			if (element) {
				element.classList.add('ie');
			}
		}
	}

	ngAfterViewInit() {
		this.route.queryParams.subscribe(params => {
		  if (this.scrollToTopSections.indexOf(params['section']) >= 0) {
			this.scrollToInitialView(params['section']);
		  }
		});
	  }
	
	scrollToInitialView(sectionName: string) {
		const offset = 100;  
		setTimeout(() => {
			this.pageScrollService.scroll({
			document: this.document,
			scrollTarget: '#' + sectionName,
			scrollOffset: offset,
			duration: 1000,
			advancedInlineOffsetCalculation: true
			});
		}, 100);
	}

	completeStepper() {
		this.stepper.reset();
		this.stepper.selected.state = 'done';
		this.stepper.selected.completed = true;
		this.stepper.steps.forEach(step => {
			step.completed = true;
		});
		const stepperElement = document.getElementById('mat-stepper');
		if (stepperElement) {
			// for new user removing the flag
			if (!this.allowNavigation && !this.returningUser) {
				this.returningUser = true;
				stepperElement.classList.remove('new-user');
			}
			stepperElement.classList.add('stepper-done');
		}
	}

	/**
	 * to check if ha form is read only or not
	 */
	getUpcomingAppointments() {
		this.spinner.startLoader();
		this.subscriptions.push(
			this.appointmentService.getUpcomingAppointments().subscribe(
				res => {
					this.spinner.stopLoader();
					const upcomingPE = res.find(
						appt =>
							appt.procedure.procedureInfo.name === PROCEDURE_CONSTANTS.PHYSICAL_EXAM &&
							appt.procedure.appointmentStatus.name === APPOINTMENT_STATUS.CONFIRMED
					);
					//console.log('upcoming pe ', upcomingPE)
					// If there is a confirmed upcoming PE - readOnly is set false
					if (upcomingPE) {
						this.readOnly = false;
						this.apptID = upcomingPE.apptID;
						this.getHealthAssessmentStatus(false);
					} else {
						this.getPastAppointments();
					}
				},
				err => {
					this.spinner.stopLoader();
					console.error(`[${HaLandingComponent.name}][${this.getUpcomingAppointments.name}]`, 'An error occurred while getting appointments ', err);
				}
			)
		);
	}

	selectEvent(event: StepperSelectionEvent) {
		this.interaction[event.selectedIndex] = true;
		this.selectedIndex = event.selectedIndex;
		event.previouslySelectedStep.completed = true;
		event.selectedStep.state = 'edit';
	}

	/**
	 * gets past appointments of user
	 */
	getPastAppointments() {
		this.spinner.startLoader();
		this.subscriptions.push(
			this.appointmentService.getPastAppointments().subscribe(
				res => {
					this.spinner.stopLoader();
					const pastPe = res.find(
						appt =>
							appt.procedure.procedureInfo.name === PROCEDURE_CONSTANTS.PHYSICAL_EXAM &&
							appt.procedure.appointmentStatus.name === APPOINTMENT_STATUS.CONFIRMED
					);
					//console.log('past pe ', pastPe)
					// If there is a confirmed past PE - readOnly is set true and ha is populated with old data
					if (pastPe) {
						this.apptID = pastPe.apptID;
						this.allowNavigation = true;
						this.getHealthAssessmentStatus(true);
					} else {
						this.showBanner = false;
						this.readOnly = true;
						this.stepper.reset();
						this.stepper.selected.state = 'done';
						this.stepper.selected.completed = true;
						this.stepper.steps.forEach(step => {
							step.completed = true;
							step.editable = false;
						});
						const stepperElement = document.getElementById('mat-stepper');
						if (stepperElement) {
							stepperElement.classList.add('stepper-done');
							stepperElement.classList.add('stepper-banner');
						}
					}
				},
				err => {
					this.spinner.stopLoader();
					console.error(`[${HaLandingComponent.name}][${this.getPastAppointments.name}]`, 'An error occurred while getting appointments ', err);
				}
			)
		);
	}

	/**
	 * Function to check the appointment date is greater than 08/19/2021
	 * @param pastPe of type Appointment
	 * @returns boolean
	 */
	isMoreThanCutOffDate(pastPe: Appointment) {
		try {
			// validation is done for date 08/20/2021, all dates before this will return false
			return new Date(pastPe.procedure.apptDate) >= new Date(PROCEDURE_CONSTANTS.CUTOFF_DATE_FOR_NEW_HA);
		} catch (e) {
			return false;
		}
	}

	/**
	 * to check if ha form is read only or not
	 */
	getHealthAssessmentStatus(pastAppt) {
		this.spinner.startLoader();
		// TODO replace api call to getHealthHistory once api is ready

		this.subscriptions.push(
			this.haService.getHealthHistoryStatus(this.apptID).subscribe(
				res => {
					//console.log(res)
					this.spinner.stopLoader();

					if (res.v2Completed || pastAppt) {
						if (!pastAppt) {
							this.showBanner = true;
							this.readOnly = false;
						} else {
							this.showBanner = false;
							this.readOnly = true;
							this.allowNavigation = true;
							this.returningUser = false;
						}
						this.stepper.reset();
						this.stepper.selected.state = 'done';
						this.stepper.selected.completed = true;
						this.stepper.steps.forEach(step => {
							step.completed = true;
							step.editable = false;
						});
						const stepperElement = document.getElementById('mat-stepper');
						if (stepperElement) {
							stepperElement.classList.add('stepper-done');
							if (res.v2Completed) {
								stepperElement.classList.add('stepper-banner');
							}
						}
					} else {
						this.healthAssessmentID = res.id;

						if (pastAppt) {
							this.allowNavigation = true;
							this.returningUser = true;
						} else if (res.v3Completed || res.lastSavedSubSection) {
							// returning user
							this.completeStepper();
							this.returningUser = true;
						} else {
							this.allowNavigation = false;
							this.navigate(['personal-info'], [], [], 'general-personal-info');
						}
						if (!this.returningUser && !this.showBanner) {
							const stepperElement = document.getElementById('mat-stepper');
							if (stepperElement) {
								stepperElement.classList.add('new-user');
							}
						}
					}
				},
				err => {
					this.spinner.stopLoader();
					console.error(`[${HaLandingComponent.name}][${this.getHealthAssessmentStatus.name}]`, 'An error occurred while getting appointments ', err);
				}
			)
		);
	}

	navigate(general, medical, lifestyle, section) {
		this.router.navigate(
			[
				{
					outlets: { general: general, medical: medical, lifestyle: lifestyle },
				},
			],
			{
				relativeTo: this.activatedRoute,
				skipLocationChange: true,
				queryParams: {
					apptID: this.apptID,
					healthAssessmentID: this.healthAssessmentID,
					readOnly: this.readOnly,
					section: section,
					returningUser: this.returningUser,
				},
			}
		);
	}

	/**
	 * Destroys the component
	 */
	ngOnDestroy() {
		document.body.classList.remove('new-ha');
		this.subscriptions.forEach(subscription => subscription.unsubscribe());
		this.subscriptions = [];
	}
}
